"use client";

import { Minus, Plus } from "lucide-react";
import { useId, useState } from "react";

export type FaqAccordionItemData = {
  id: number;
  question: string;
  answer: string;
  defaultExpanded: boolean;
};

type FaqAccordionItemProps = {
  item: FaqAccordionItemData;
};

export function FaqAccordionItem({ item }: FaqAccordionItemProps) {
  const [isExpanded, setIsExpanded] = useState(item.defaultExpanded);
  const answerId = useId();

  return (
    <article
      className={`overflow-hidden rounded-2xl border bg-white shadow-[0_18px_46px_-36px_rgba(15,23,42,0.55)] transition-colors duration-150 ${
        isExpanded ? "border-green-200" : "border-slate-200"
      }`}
    >
      <h3>
        <button
          type="button"
          aria-expanded={isExpanded}
          aria-controls={answerId}
          onClick={() => setIsExpanded((current) => !current)}
          className="flex min-h-[78px] w-full items-center gap-4 px-5 py-4 text-left transition-colors duration-150 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-[-4px] focus-visible:outline-green-700 sm:min-h-[88px] sm:gap-5 sm:px-6"
        >
          <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-[#08A62F] text-lg font-black leading-none text-white shadow-[0_10px_22px_-14px_rgba(8,166,47,0.95)] sm:h-11 sm:w-11">
            {item.id}
          </span>
          <span className="min-w-0 flex-1 text-[17px] font-black leading-snug text-slate-950 sm:text-xl">
            {item.question}
          </span>
          <span
            className={`flex h-9 w-9 shrink-0 items-center justify-center rounded-full transition-colors duration-150 ${
              isExpanded
                ? "bg-[#08A62F] text-white"
                : "border border-slate-200 bg-slate-50 text-slate-950 shadow-sm"
            }`}
            aria-hidden
          >
            {isExpanded ? (
              <Minus className="h-5 w-5" strokeWidth={3} />
            ) : (
              <Plus className="h-5 w-5" strokeWidth={2.8} />
            )}
          </span>
        </button>
      </h3>

      {isExpanded ? (
        <div id={answerId} className="bg-green-50/65 px-5 py-5 sm:px-6 sm:py-5">
          <p className="text-[15px] font-medium leading-7 text-slate-700 sm:text-base">{item.answer}</p>
        </div>
      ) : null}
    </article>
  );
}
